import { useState } from 'react';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { apiGet, apiPostJson, apiDelete, ApiError } from '../api/client';
import { formatMkd, formatDate, formatNumber } from '../lib/format';
import ConfirmDialog from '../components/ConfirmDialog';
import { btn } from '../components/FilterBar';
import { useToast } from '../components/Toast';

/**
 * Shpenzimet Automatike — the recurring schedules driving the nightly job.
 * Ports frmShpenzimetAutomatike.
 *
 * One action, labelled "Deactive", which deletes the row — the desktop button,
 * prompt and message verbatim (frmShpenzimetAutomatike.cs:187-201). The wording
 * says deactivate and the effect is a permanent DELETE; `IsActive` exists but
 * the desktop never sets it, so neither does this.
 */

interface ScheduledRow {
  ID: number;
  Data: string;
  biznesiID: number;
  biznesi: string;
  kategoria: string | null;
  artikulli: string | null;
  sasia: number | null;
  Cmimi: number;
  Periudha: string | null;
  NextExecutionDate: string;
  LastExecutedDate: string | null;
  IsActive: number;
}

export default function ShpenzimetAutomatike() {
  const queryClient = useQueryClient();
  const toast = useToast();

  const [search, setSearch] = useState('');
  const [deleting, setDeleting] = useState<ScheduledRow | null>(null);

  const listQuery = useQuery({
    queryKey: ['automatike', search],
    queryFn: () => apiGet<{ rows: ScheduledRow[] }>('/shpenzimet/automatike', { search }),
    placeholderData: (prev) => prev,
  });

  function refresh() {
    void queryClient.invalidateQueries({ queryKey: ['automatike'] });
  }

  const deleteMutation = useMutation({
    mutationFn: (id: number) => apiDelete(`/shpenzimet/automatike/${id}`),
    onSuccess: () => {
      // frmShpenzimetAutomatike.cs:201 — the message the desktop shows after
      // the DELETE succeeds.
      toast.success('Shpenzimi u çaktivizua.');
      refresh();
    },
    onError: (err) => toast.error(err instanceof ApiError ? err.message : 'Veprimi dështoi.'),
  });

  const runMutation = useMutation({
    mutationFn: () => apiPostJson<{ processed: number }>('/shpenzimet/automatike/ekzekuto', {}),
    onSuccess: (result) => {
      toast.success(
        result.processed > 0
          ? `U shtuan ${result.processed} shpenzime.`
          : 'Asnjë shpenzim nuk ishte për t’u shtuar.',
      );
      refresh();
    },
    onError: (err) => toast.error(err instanceof ApiError ? err.message : 'Ekzekutimi dështoi.'),
  });

  const rows = listQuery.data?.rows ?? [];

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-xl font-semibold">Shpenzimet automatike</h1>
        <p className="text-sm text-muted">
          Shpenzimet që shtohen vetvetiu sipas periudhës, për të gjitha bizneset
        </p>
      </div>

      <div className="rounded-xl bg-white p-4">
        <div className="flex flex-wrap items-end gap-3">
          <div className="min-w-52 flex-1">
            <label className="mb-1 block text-xs font-medium text-muted" htmlFor="auto-search">
              Kërko artikull, kategori ose biznes
            </label>
            <input
              id="auto-search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Shkruani për të filtruar…"
              className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-100"
            />
          </div>
          <button
            type="button"
            onClick={() => runMutation.mutate()}
            disabled={runMutation.isPending}
            className={btn.secondary}
            title="Kontrollon nëse ka shpenzime të vonuara dhe i shton tani"
          >
            {runMutation.isPending ? 'Duke ekzekutuar…' : 'Ekzekuto tani'}
          </button>
        </div>
      </div>

      <div className="overflow-hidden rounded-xl bg-white">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[900px] text-sm">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50 text-left text-xs uppercase tracking-wide text-muted">
                <th className="px-4 py-3 font-semibold">Statusi</th>
                <th className="px-4 py-3 font-semibold">Biznesi</th>
                <th className="px-4 py-3 font-semibold">Kategoria</th>
                <th className="px-4 py-3 font-semibold">Artikulli</th>
                <th className="px-4 py-3 text-right font-semibold">Sasia</th>
                <th className="px-4 py-3 text-right font-semibold">Çmimi</th>
                <th className="px-4 py-3 font-semibold">Periudha</th>
                <th className="px-4 py-3 font-semibold">Ekzekutimi tjetër</th>
                <th className="px-4 py-3 text-right font-semibold">Veprime</th>
              </tr>
            </thead>
            <tbody>
              {listQuery.isLoading && (
                <tr>
                  <td colSpan={9} className="px-4 py-10 text-center text-muted">
                    Duke ngarkuar…
                  </td>
                </tr>
              )}

              {!listQuery.isLoading && rows.length === 0 && (
                <tr>
                  <td colSpan={9} className="px-4 py-10 text-center text-muted">
                    Nuk ka shpenzime automatike.
                  </td>
                </tr>
              )}

              {rows.map((row) => {
                const active = Number(row.IsActive) === 1;
                return (
                  <tr
                    key={row.ID}
                    className={`border-b border-slate-100 last:border-0 hover:bg-slate-50 ${
                      active ? '' : 'opacity-60'
                    }`}
                  >
                    <td className="px-4 py-2.5">
                      <span
                        className={`rounded-full px-2 py-0.5 text-xs font-medium ${
                          active
                            ? 'bg-emerald-100 text-emerald-800'
                            : 'bg-slate-200 text-slate-600'
                        }`}
                      >
                        {active ? 'Aktiv' : 'Joaktiv'}
                      </span>
                    </td>
                    <td className="px-4 py-2.5">{row.biznesi}</td>
                    <td className="px-4 py-2.5">{row.kategoria ?? '—'}</td>
                    <td className="px-4 py-2.5">{row.artikulli ?? '—'}</td>
                    <td className="tnum px-4 py-2.5 text-right">
                      {row.sasia == null ? '' : formatNumber(row.sasia)}
                    </td>
                    <td className="tnum px-4 py-2.5 text-right font-medium">
                      {formatMkd(row.Cmimi)}
                    </td>
                    <td className="px-4 py-2.5 text-muted">{row.Periudha ?? ''}</td>
                    <td className="px-4 py-2.5 whitespace-nowrap">
                      {formatDate(row.NextExecutionDate)}
                    </td>
                    <td className="px-4 py-2.5">
                      <div className="flex justify-end gap-1">
                        {/* The desktop's single "Deactive" button. */}
                        <button
                          type="button"
                          onClick={() => setDeleting(row)}
                          className="rounded-lg px-2 py-1 text-xs font-medium text-red-600 hover:bg-red-50"
                        >
                          Deactive
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {deleting && (
        <ConfirmDialog
          title="Confirm"
          // frmShpenzimetAutomatike.cs:192, word for word.
          message="Jeni i sigurt që dëshironi ta çaktivizoni këtë shpenzim automatik?"
          confirmLabel="Po"
          destructive
          busy={deleteMutation.isPending}
          onCancel={() => setDeleting(null)}
          onConfirm={() => {
            deleteMutation.mutate(deleting.ID);
            setDeleting(null);
          }}
        />
      )}
    </div>
  );
}
